import Layout from "../hocs/Layout";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  get_products,
  get_filtered_products,
  search_products,
} from "../redux/actions/products";
import Banner from "../components/home/Banner";
import ProductCard from "../components/product/ProductCard";
import CountryChips from "../components/country";
import { Helmet } from "react-helmet";
import UploadButton from "./Upload";

const Home = ({
  get_products,
  get_filtered_products,
  search_products,
  products,
  filtered_products,
  products_search,
  isAuthenticated,
}) => {
  const location = useLocation();
  const [filtered, setFiltered] = useState(false);

  // We keep the same form data keys as before
  // but now "category_id" is effectively "country_id"
  const [formData, setFormData] = useState({
    country_code: "0",
    price_range: "Todos",
    sortBy: "created",
    order: "desc",
  });
  const [showSizeOptions] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [columnsMobile, setColumnsMobile] = useState(2);
  const [showSubscribe, setShowSubscribe] = useState("");
  const { country_code, price_range, sortBy, order } = formData;

  // On mount or when URL changes, check if there's a search term
  useEffect(() => {
    window.scrollTo(0, 0);
    const query = new URLSearchParams(location.search);
    const searchTerm = query.get("search");

    if (searchTerm) {
      // If there's a search term, dispatch the search action
      search_products(searchTerm);
      setFiltered(true);
    } else {
      // Otherwise, load all products
      get_products();
      setFiltered(false);
    }
  }, [location.search, get_products, search_products]);

  // "onChange" handles updates to filters (country, size, etc.)
  const onChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === "size") {
      // Handle size checkboxes
      let updatedSizes = [...selectedSizes];
      if (checked) {
        updatedSizes.push(value);
      } else {
        updatedSizes = updatedSizes.filter((size) => size !== value);
      }
      setSelectedSizes(updatedSizes);

      // Filter products based on the new size selection
      get_filtered_products(
        country_code,
        price_range,
        sortBy,
        order,
        updatedSizes
      );
      setFiltered(true);
    } else {
      // Handle other filters (country, price range, etc.)
      const newFormData = { ...formData, [name]: value };
      setFormData(newFormData);

      get_filtered_products(
        newFormData.country_code,
        newFormData.price_range,
        newFormData.sortBy,
        newFormData.order,
        selectedSizes
      );
      setFiltered(true);
    }
  };

  const showProducts = () => {
    const query = new URLSearchParams(location.search);
    const searchTerm = query.get("search");
    let display = [];
  
    if (searchTerm) {
      display = products_search;
    } else if (filtered) {
      display = filtered_products;
    } else {
      display = products;
    }
  
    return (
      <div
        className={`
          transition-all duration-500
          grid 
          gap-4 sm:gap-6 
          ${
            columnsMobile === 1
              ? "grid-cols-1"
              : columnsMobile === 2
              ? "grid-cols-2"
              : "grid-cols-4"
          }
          sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4
        `}
      >
        {display && display.length > 0 ? (
          display.map((product, index) => (
            <ProductCard key={index} product={product} viewMode={columnsMobile} />
          ))
        ) : (
          <p className="col-span-full text-center">
            {filtered
              ? "There are no products for the selected country."
              : "No products found."}
          </p>
        )}
      </div>
    );
  };
  

  // Resets to all products
  const resetProducts = () => {
    get_products();
  };
  const handleCountryChange = (code) => {
    onChange({ target: { name: "country_code", value: code } });
  };

  return (
    <Layout>
      <Helmet>
        <title>Histori AI - Init</title>
        <meta
          name="description"
          content="Descubre productos vintage de alta calidad en Histori.ai. Encuentra lo que buscas entre nuestras diversas categorías y productos."
        />

        <meta
          name="keywords"
          content="tienda, antigüedades, vintage, retro, calidad, categorías, productos"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Histori.ai - Productos Retro de Calidad"
        />

        <meta
          property="og:description"
          content="Descubre productos vintage de alta calidad en Histori.ai. Encuentra lo que buscas entre nuestras diversas categorías y productos."
        />

        <meta
          property="og:image"
          content="https://histori.ai/path/a/tu-imagen.jpg"
        />

        <meta property="og:url" content="https://histori.ai" />

        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="Histori.ai - Productos Retro de Calidad"
        />

        <meta
          name="twitter:description"
          content="Descubre productos vintage de alta calidad en Histori.ai. ¡Explora nuestras categorías y encuentra el producto perfecto!"
        />

        <meta
          name="twitter:image"
          content="https://histori.ai/path/a/tu-imagen.jpg"
        />
      </Helmet>

      <div className="bg-white min-h-screen flex flex-col">
        <Banner />

        <CountryChips
          selectedCountryCode={country_code}
          onChangeCountryCode={handleCountryChange}
        />

        {/* Botones para cambiar la vista en móvil (se ocultan en desktop) */}
        <div className="flex justify-end items-center gap-2 px-4 sm:px-10 lg:px-16 mb-4 sm:hidden">
          <button
            onClick={() => setColumnsMobile(1)}
            className={`px-3 py-1 rounded border text-sm ${
              columnsMobile === 1
                ? "bg-black text-white border-black"
                : "bg-white text-black border-gray-300"
            }`}
          >
            1 col
          </button>
          <button
            onClick={() => setColumnsMobile(2)}
            className={`px-3 py-1 rounded border text-sm ${
              columnsMobile === 2
                ? "bg-black text-white border-black"
                : "bg-white text-black border-gray-300"
            }`}
          >
            2 cols
          </button>
          <button
            onClick={() => setColumnsMobile(4)}
            className={`px-3 py-1 rounded border text-sm ${
              columnsMobile === 4
                ? "bg-black text-white border-black"
                : "bg-white text-black border-gray-300"
            }`}
          >
            4 cols
          </button>
        </div>

        <UploadButton resetProducts={resetProducts} />

        <section className="px-4 sm:px-8 flex-1">
          <div className="container mx-auto h-full">
            {!products ? (
              <div className="flex items-center justify-center min-h-[30vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500"></div>
              </div>
            ) : (
              showProducts()
            )}
          </div>
        </section>

       
      </div>
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.Auth.isAuthenticated,
  products: state.Products.products,
  filtered_products: state.Products.filtered_products,
  products_search: state.Products.search_products,
});

export default connect(mapStateToProps, {
  get_products,
  get_filtered_products,
  search_products,
})(Home);